// File: src/components/admin/EmailConfigView.js
import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Tabs, Tab, TextField, Button, Grid,
    Accordion, AccordionSummary, AccordionDetails, Chip, Alert,
    CircularProgress, Select, MenuItem, FormControl, InputLabel,
    Dialog, DialogTitle, DialogContent, DialogActions, Divider,
    Autocomplete
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, Save as SaveIcon, Preview as PreviewIcon, RestartAlt as DefaultIcon } from '@mui/icons-material';
import { adminApi, handleAdminApiError } from '../../services/adminApi';
import AiGenerateButton from './common/AiGenerateButton';

const SUPPORTED_LANGUAGES = ['en', 'de', 'fr'];
const EVENT_TYPES = [
    'user_signup',
    'subscription_created',
    'subscription_cancelled',
    'limits_reached',
    'first_campaign_created',
    'trial_expiring',
    'trial_expired',
    'trial_expired_reminder_3d',
    'trial_expired_reminder_7d'
];

const DEFAULT_PROMPTS = {
    user_signup: 'Write a warm welcome email to {{userName}} who just signed up for Defo Ads. Mention they can start creating ad campaigns right away with AI assistance. Keep it brief and encouraging. Include a CTA to create their first campaign.',
    subscription_created: 'Write a confirmation email to {{userName}} thanking them for subscribing to the {{planName}} plan. Mention key features they now have access to: {{features}}. Include a CTA to explore their dashboard.',
    subscription_cancelled: 'Write a friendly email to {{userName}} acknowledging their subscription cancellation. Mention their access continues until {{expiresAt}}. Ask if there is anything that could be improved. Keep tone respectful, not desperate.',
    limits_reached: 'Write a helpful email to {{userName}} letting them know they have reached their {{limitType}} limit ({{used}}/{{limit}}). Suggest upgrading to {{nextPlanName}} for higher limits: {{nextPlanFeatures}}. Keep tone helpful, not pushy.',
    first_campaign_created: 'Write a congratulatory email to {{userName}} for creating their first campaign "{{campaignName}}". Give a brief tip about next steps (adding ad groups, keywords, syncing to Google/Microsoft Ads). Keep it short and motivating.',
    trial_expiring: 'Write a helpful email to {{userName}} letting them know their Defo Ads trial ends in {{daysLeft}} days. Mention what they have built: {{campaignCount}} campaigns and {{adCount}} ads. Emphasise that upgrading preserves everything and takes one click. Include a clear CTA to upgrade. Keep tone encouraging, not pressuring.',
    trial_expired: 'Write a warm, clear email to {{userName}} informing them that their Defo Ads trial has ended. Mention they have {{campaignCount}} campaigns and {{adCount}} ads safely preserved in read-only mode. Highlight the 20% discount available for the next 72 hours if {{discountEligible}} is true. Include two CTAs: one to upgrade directly ({{checkoutUrl}}) and one to browse in read-only mode. Keep the tone friendly and helpful, not alarming.',
    trial_expired_reminder_3d: 'Write a gentle follow-up email to {{userName}} reminding them that their Defo Ads trial ended 3 days ago. Their {{campaignCount}} campaigns are safe but paused. If {{discountEligible}} is true, mention the discount expires soon. Focus on what they are missing: AI generation, sync, editing. Include a clear upgrade CTA.',
    trial_expired_reminder_7d: 'Write a final re-engagement email to {{userName}}. It has been a week since their trial ended. Their {{campaignCount}} campaigns and {{adCount}} ads are still safe. Take a softer, no-pressure tone — "whenever you are ready" — but include a clear upgrade CTA. Mention that their data will stay safe indefinitely.',
};

const EmailConfigView = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    const [masterConfig, setMasterConfig] = useState({ html: '', translations: {} });
    const [eventPrompts, setEventPrompts] = useState({});

    // Users list for preview selection
    const [users, setUsers] = useState([]);

    // Preview Dialog State
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewEventType, setPreviewEventType] = useState(null);
    const [previewLanguage, setPreviewLanguage] = useState('en');
    const [selectedUser, setSelectedUser] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewResult, setPreviewResult] = useState(null);

    useEffect(() => {
        loadConfig();
        loadUsers();
    }, []);

    const loadConfig = async () => {
        try {
            setLoading(true);
            const data = await adminApi.getEmailConfig();
            setMasterConfig(data.masterConfig || { html: '', translations: {} });
            setEventPrompts(data.eventPrompts || {});
            setError(null);
        } catch (err) {
            const { message } = handleAdminApiError(err);
            setError(`Failed to load config: ${message}`);
        } finally {
            setLoading(false);
        }
    };

    const loadUsers = async () => {
        try {
            const data = await adminApi.getUsers({ limit: 100 });
            setUsers(data.users || []);
        } catch (err) {
            console.error('Failed to load users:', err);
        }
    };

    const handleSaveMaster = async () => {
        try {
            setSaving(true);
            setSuccessMessage(null);
            await adminApi.updateMasterConfig(masterConfig);
            setSuccessMessage('Master template saved successfully.');
        } catch (err) {
            const { message } = handleAdminApiError(err);
            setError(`Failed to save master config: ${message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleSavePrompt = async (eventType, prompt) => {
        try {
            setSaving(true);
            setSuccessMessage(null);
            await adminApi.updateEventPrompt(eventType, prompt);
            setEventPrompts(prev => ({ ...prev, [eventType]: prompt }));
            setSuccessMessage(`Prompt for ${eventType} saved.`);
        } catch (err) {
            const { message } = handleAdminApiError(err);
            setError(`Failed to save prompt: ${message}`);
        } finally {
            setSaving(false);
        }
    };

    const openPreviewDialog = (eventType) => {
        setPreviewEventType(eventType);
        setSelectedUser(null);
        setPreviewLanguage('en');
        setPreviewResult(null);
        setPreviewOpen(true);
    };

    const handleGeneratePreview = async () => {
        try {
            setPreviewLoading(true);
            const result = await adminApi.previewEmail({
                eventType: previewEventType,
                language: previewLanguage,
                userId: selectedUser?.uid || null,
                prompt: eventPrompts[previewEventType]
            });
            setPreviewResult(result);
        } catch (err) {
            const { message } = handleAdminApiError(err);
            setError(`Failed to generate preview: ${message}`);
        } finally {
            setPreviewLoading(false);
        }
    };

    if (loading) return <CircularProgress />;

    return (
        <Box>
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            {successMessage && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>{successMessage}</Alert>}

            <Paper sx={{ width: '100%', mb: 2 }}>
                <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
                    <Tab label="Master Template" />
                    <Tab label="Event Prompts" />
                </Tabs>

                <Box sx={{ p: 3 }}>
                    {activeTab === 0 && (
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Typography variant="h6">Master HTML Template</Typography>
                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                    Use <code>{'{{header}}'}</code>, <code>{'{{body}}'}</code>, and <code>{'{{footer}}'}</code> placeholders.
                                </Typography>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={10}
                                    value={masterConfig.html}
                                    onChange={(e) => setMasterConfig({ ...masterConfig, html: e.target.value })}
                                    variant="outlined"
                                    sx={{ fontFamily: 'monospace' }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ mt: 2 }}>Shared Translations</Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Define Header/Footer content for each language.
                                </Typography>
                                {SUPPORTED_LANGUAGES.map(lang => (
                                    <Accordion key={lang} sx={{ mt: 1 }}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography sx={{ textTransform: 'uppercase' }}>{lang}</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <TextField
                                                label="Header HTML"
                                                fullWidth
                                                multiline
                                                rows={3}
                                                value={masterConfig.translations?.[lang]?.header || ''}
                                                onChange={(e) => {
                                                    const newTrans = { ...masterConfig.translations };
                                                    if (!newTrans[lang]) newTrans[lang] = {};
                                                    newTrans[lang].header = e.target.value;
                                                    setMasterConfig({ ...masterConfig, translations: newTrans });
                                                }}
                                                sx={{ mb: 2 }}
                                            />
                                            <TextField
                                                label="Footer HTML"
                                                fullWidth
                                                multiline
                                                rows={3}
                                                value={masterConfig.translations?.[lang]?.footer || ''}
                                                onChange={(e) => {
                                                    const newTrans = { ...masterConfig.translations };
                                                    if (!newTrans[lang]) newTrans[lang] = {};
                                                    newTrans[lang].footer = e.target.value;
                                                    setMasterConfig({ ...masterConfig, translations: newTrans });
                                                }}
                                            />
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </Grid>

                            <Grid item xs={12}>
                                <Button
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    onClick={handleSaveMaster}
                                    disabled={saving}
                                >
                                    Save Master Config
                                </Button>
                            </Grid>
                        </Grid>
                    )}

                    {activeTab === 1 && (
                        <Grid container spacing={3}>
                            {EVENT_TYPES.map(eventType => (
                                <Grid item xs={12} key={eventType}>
                                    <Accordion defaultExpanded={false}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between', pr: 2 }}>
                                                <Typography sx={{ fontWeight: 'bold' }}>{eventType}</Typography>
                                                <Chip label={eventPrompts[eventType] ? 'Configured' : 'Missing'} color={eventPrompts[eventType] ? 'success' : 'warning'} size="small" />
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Box sx={{ mb: 2 }}>
                                                <AiGenerateButton
                                                    onSuccess={(generated) => {
                                                        const newVal = typeof generated === 'string' ? generated : generated.prompt;
                                                        setEventPrompts(prev => ({ ...prev, [eventType]: newVal }));
                                                    }}
                                                    promptContext={`Generate an AI system prompt for an email notification service.
Event Type: ${eventType}
Context Data Available: user (name, email, language), subscription (active, planName, expiresAt, features), usage (dailyTokensUsed/Limit, campaignsCreated/Limit), upgradeOffer (nextPlan details), trial (trialEndsAt, daysRemaining, isExpired).
Goal: The prompt should instruct the LLM to write a friendly, professional email body in HTML format (no html/body tags, just content) for this event. If this is a limit-reached email, nudge the user to upgrade with the upgradeOffer details. If this is a trial event, reference trial expiry timing and encourage upgrading to a paid plan.`}
                                                    buttonLabel="Generate Prompt with AI"
                                                />
                                            </Box>
                                            <TextField
                                                fullWidth
                                                multiline
                                                minRows={6}
                                                label="AI Prompt"
                                                value={eventPrompts[eventType] || ''}
                                                onChange={(e) => setEventPrompts(prev => ({ ...prev, [eventType]: e.target.value }))}
                                                helperText="This prompt is sent to the AI at runtime to generate the email body. Context includes: user, subscription, usage, upgradeOffer."
                                            />
                                            <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                                <Button
                                                    variant="contained"
                                                    startIcon={<SaveIcon />}
                                                    onClick={() => handleSavePrompt(eventType, eventPrompts[eventType])}
                                                    disabled={saving}
                                                >
                                                    Save Prompt
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<PreviewIcon />}
                                                    onClick={() => openPreviewDialog(eventType)}
                                                >
                                                    Preview Email
                                                </Button>
                                                {DEFAULT_PROMPTS[eventType] && (
                                                    <Button
                                                        variant="outlined"
                                                        color="secondary"
                                                        startIcon={<DefaultIcon />}
                                                        onClick={() => setEventPrompts(prev => ({ ...prev, [eventType]: DEFAULT_PROMPTS[eventType] }))}
                                                    >
                                                        Apply Default
                                                    </Button>
                                                )}
                                            </Box>
                                        </AccordionDetails>
                                    </Accordion>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Box>
            </Paper>

            {/* Email Preview Dialog */}
            <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="lg" fullWidth>
                <DialogTitle>Email Preview - {previewEventType}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        {/* User Selector */}
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                options={users}
                                getOptionLabel={(option) => `${option.displayName || option.email} (${option.uid?.substring(0, 8)}...)`}
                                value={selectedUser}
                                onChange={(e, newValue) => setSelectedUser(newValue)}
                                renderInput={(params) => (
                                    <TextField {...params} label="Select User (Real Data)" size="small" />
                                )}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.uid}>
                                        <Box>
                                            <Typography variant="body2">{option.displayName || option.email}</Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {option.email} • {option.subscriptionPlan || 'No Plan'}
                                            </Typography>
                                        </Box>
                                    </li>
                                )}
                            />
                            <Typography variant="caption" color="textSecondary">
                                Leave empty for mock data
                            </Typography>
                        </Grid>

                        {/* Language Selector */}
                        <Grid item xs={12} sm={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Language</InputLabel>
                                <Select
                                    value={previewLanguage}
                                    label="Language"
                                    onChange={(e) => setPreviewLanguage(e.target.value)}
                                >
                                    {SUPPORTED_LANGUAGES.map(l => (
                                        <MenuItem key={l} value={l}>{l.toUpperCase()}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Generate Button */}
                        <Grid item xs={12} sm={3}>
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={handleGeneratePreview}
                                disabled={previewLoading}
                                startIcon={previewLoading ? <CircularProgress size={20} /> : <PreviewIcon />}
                            >
                                Generate
                            </Button>
                        </Grid>

                        {/* Results */}
                        {previewResult && (
                            <>
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }} />
                                </Grid>

                                {/* Context Used - Collapsible */}
                                <Grid item xs={12}>
                                    <Accordion defaultExpanded={false}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography variant="subtitle2">Context Data Used</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Box sx={{
                                                bgcolor: '#f5f5f5',
                                                p: 2,
                                                borderRadius: 1,
                                                fontFamily: 'monospace',
                                                fontSize: '0.8rem',
                                                maxHeight: 200,
                                                overflow: 'auto'
                                            }}>
                                                <pre>{JSON.stringify(previewResult.contextUsed, null, 2)}</pre>
                                            </Box>
                                        </AccordionDetails>
                                    </Accordion>
                                </Grid>

                                {/* Subject */}
                                <Grid item xs={12}>
                                    <Typography variant="subtitle1">
                                        <strong>Subject:</strong> {previewResult.subject}
                                    </Typography>
                                </Grid>

                                {/* Email Preview */}
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" gutterBottom>Email Preview:</Typography>
                                    <Box
                                        sx={{
                                            border: '1px solid #ddd',
                                            borderRadius: 1,
                                            p: 2,
                                            bgcolor: 'white',
                                            maxHeight: 400,
                                            overflow: 'auto'
                                        }}
                                        dangerouslySetInnerHTML={{ __html: previewResult.fullHtml }}
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreviewOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default EmailConfigView;
